import { useEffect, useState } from 'react';

import { authFetchWithExchangeRetry } from '../../lib/auth-fetch-with-exchange-retry.js';
import { isCancellation } from '../../lib/is-cancellation.js';
import { watchPageLeave } from '../../lib/page-leave.js';
import { reportConsultError } from '../../lib/report-consult-error.js';

export type Consultant = {
  name: string;
  scheduleUrl: string;
};

/**
 * Narrow one element of the `/consult-scheduling/consultants` response to a renderable consultant.
 *
 * @remarks The array was accepted on `Array.isArray` alone with no per-element check, while the two
 * scalars beside it each got a `typeof === 'string' ? …trim() : ''` guard — the file validated its
 * scalars and not its array. `consultant.name` is used three ways at once in `one-to-one-tab.tsx`:
 * as the React `key`, as the `Card title`, and interpolated into the frame title as
 * `Schedule with ${name}`. A missing name therefore produced `key={undefined}` and an iframe the
 * member saw titled "Schedule with undefined".
 *
 * `scheduleUrl` is required non-empty as well: `buildBookingUrl` returns `null` for an unusable
 * value so a missing one degrades to a frame that never loads rather than throwing, but a Card
 * offering a consultant who cannot be booked is not worth rendering.
 */
export function isRenderableConsultant(value: unknown): value is Consultant {
  if (typeof value !== 'object' || value === null) return false;
  if (!('name' in value) || typeof value.name !== 'string' || value.name.trim() === '')
    return false;
  return (
    'scheduleUrl' in value &&
    typeof value.scheduleUrl === 'string' &&
    value.scheduleUrl.trim() !== ''
  );
}

/** Read a trimmed string field off an unvalidated response body, `''` when it is not one. */
function readTrimmedString(source: object, key: string): string {
  if (!(key in source)) return '';
  const value = (source as Record<string, unknown>)[key];
  return typeof value === 'string' ? value.trim() : '';
}

export type OneToOneConsultantsState = {
  consultants: Consultant[];
  /** FFS `initial` stage Zoom Scheduler URL; empty when the program has none. */
  initialConsultScheduleUrl: string;
  /** Program follow-up (and non-FFS "schedule a consult") Zoom Scheduler URL. */
  followUpConsultScheduleUrl: string;
  consultantsStatus: 'idle' | 'loading' | 'loaded' | 'error';
  consultantsError: string | null;
};

/**
 * Loads the 1:1 tab's scheduling targets: the program's initial/follow-up Zoom Scheduler URLs and
 * any named consultants.
 *
 * @remarks Lifted verbatim out of the vendor page; `enabled` mirrors its
 * `scheduleOneToOneTabVisible` guard, so the request is gated on the tab being OFFERED, not active.
 */
export function useOneToOneConsultants(params: {
  jwt: string | null;
  setJwt: (jwt: string) => void;
  token: string | undefined;
  apiBase: string;
  enabled: boolean;
}): OneToOneConsultantsState {
  const { jwt, setJwt, token, apiBase, enabled } = params;
  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [initialConsultScheduleUrl, setInitialConsultScheduleUrl] = useState<string>('');
  const [followUpConsultScheduleUrl, setFollowUpConsultScheduleUrl] = useState<string>('');
  const [consultantsStatus, setConsultantsStatus] = useState<
    'idle' | 'loading' | 'loaded' | 'error'
  >('idle');
  const [consultantsError, setConsultantsError] = useState<string | null>(null);

  useEffect(() => {
    if (!jwt || !enabled) return;

    const controller = new AbortController();
    setConsultantsStatus('loading');
    setConsultantsError(null);

    const load = authFetchWithExchangeRetry({
      magicLinkToken: token,
      setJwt,
      apiBase,
      jwt,
      url: `${apiBase}/consult-scheduling/consultants`,
      init: { signal: controller.signal },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load consultants');
        // `unknown`, not a shape cast: the cast was the only thing asserting the element type of
        // `consultants`, and it asserted it of data nothing had checked.
        return res.json() as Promise<unknown>;
      })
      .then((data) => {
        const body: object = typeof data === 'object' && data !== null ? data : {};
        const rawConsultants = 'consultants' in body ? body.consultants : undefined;
        setConsultants(
          Array.isArray(rawConsultants)
            ? rawConsultants.filter(isRenderableConsultant).map((consultant) => ({
                name: consultant.name.trim(),
                scheduleUrl: consultant.scheduleUrl.trim(),
              }))
            : [],
        );
        setInitialConsultScheduleUrl(readTrimmedString(body, 'initialConsultScheduleUrl'));
        setFollowUpConsultScheduleUrl(readTrimmedString(body, 'followUpConsultScheduleUrl'));
        setConsultantsStatus('loaded');
      });
    const pageLeave = watchPageLeave(load);
    load.catch((err: unknown) => {
      // The signal, not the error name: Safari calls a cancelled load `TypeError: Load
      // failed`, which is indistinguishable from a real failure by wording alone.
      if (isCancellation(err, controller.signal)) return;
      if (!pageLeave.leaving) reportConsultError('consultants-fetch', err);
      setConsultantsStatus('error');
      setConsultantsError('Unable to load consultants. Please try again later.');
    });

    return () => controller.abort();
    // Vendor dependency list, unchanged: `setJwt` and `apiBase` are stable for the page's lifetime.
  }, [jwt, enabled, token]);

  return {
    consultants,
    initialConsultScheduleUrl,
    followUpConsultScheduleUrl,
    consultantsStatus,
    consultantsError,
  };
}
